import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useAuth';
import { authApi } from '@/lib/auth-api';

const DISMISS_KEY = 'cadencez.verifyBanner.dismissed';

export function UnverifiedEmailBanner() {
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  const [dismissed, setDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1',
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.emailVerified || dismissed || !user.email) return null;

  const onResend = async () => {
    setSending(true);
    try {
      await authApi.resendVerification(user.email!);
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  const onDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
      <div className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            <Trans
              i18nKey="common.email_verify_banner"
              values={{ email: user.email }}
              components={[<strong key="e" />]}
              defaults="Please confirm your email at <0>{{email}}</0> to unlock all features."
            />
          </span>
        </div>
        <div className="flex items-center gap-2">
          {sent ? (
            <span className="text-xs text-amber-900 dark:text-amber-100">{t('common.sent_check_inbox', { defaultValue: 'Sent — check your inbox.' })}</span>
          ) : (
            <button
              onClick={onResend}
              disabled={sending}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-60"
            >
              {sending && <Loader2 className="w-3 h-3 animate-spin" />}
              {t('common.resend_email', { defaultValue: 'Resend email' })}
            </button>
          )}
          <button
            onClick={onDismiss}
            className="p-1 rounded-md text-amber-700 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
            aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
